import { useState, useCallback } from "react";
import { Container, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const CATEGORIES = [
  "Foundation Models",
  "AI Applications",
  "Inference / Hardware",
  "Data Infrastructure",
  "Vector DBs",
  "Orchestration",
  "MLOps / Observability",
];

const COLORS = {
  "Foundation Models":     "#1a1a2e",
  "AI Applications":       "#dc2626",
  "Inference / Hardware":  "#059669",
  "Data Infrastructure":   "#7c3aed",
  "Vector DBs":            "#4f46e5",
  "Orchestration":         "#0891b2",
  "MLOps / Observability": "#d97706",
};

const rawData = [
  // ── Foundation Models ──────────────────────────────────────────────────────
  {
    name: "OpenAI",
    category: "Foundation Models",
    totalFundingM: 253000,
    valuationM: 852000,
    employees: 6000,
    founded: 2015,
    latestRound: "$122B (Apr 2026)",
    insight: "Most capitalized AI company in history. 3.4x multiple reflects the sheer scale of compute required to maintain the frontier. API distribution plus the RLHF data flywheel compound into a structural advantage that is hard to erode.",
  },
  {
    name: "Anthropic",
    category: "Foundation Models",
    totalFundingM: 46700,
    valuationM: 380000,
    employees: 2000,
    founded: 2021,
    latestRound: "$30B Series G (Feb 2026)",
    insight: "8.1x multiple on $46.7B raised. More capital-efficient than OpenAI at comparable scale. Constitutional AI and the safety narrative are genuine enterprise differentiators, not just optics.",
  },
  {
    name: "xAI",
    category: "Foundation Models",
    totalFundingM: 22130,
    valuationM: 230000,
    employees: 2000,
    founded: 2023,
    latestRound: "$20B Series E (Jan 2026)",
    insight: "10.4x multiple and the fastest trajectory to $100B+ of any AI company. X/Twitter distribution is undermodeled by most analysts. Grok embedded across 680M accounts is a GTM advantage nobody else has.",
  },
  {
    name: "Mistral AI",
    category: "Foundation Models",
    totalFundingM: 3190,
    valuationM: 14000,
    employees: 400,
    founded: 2023,
    latestRound: "1.7B EUR Series C (Sep 2025)",
    insight: "4.4x multiple. Most capital-efficient frontier model company. EU-native compliance positioning is a real wedge as the AI Act enforcement ramps. Open weights strategy keeps them relevant as the model commodity debate plays out.",
  },
  {
    name: "Cohere",
    category: "Foundation Models",
    totalFundingM: 1600,
    valuationM: 7000,
    employees: 500,
    founded: 2019,
    latestRound: "$500M (Aug 2025)",
    insight: "4.4x multiple. Enterprise-only go-to-market limits the ceiling but dramatically improves unit economics. Focus on retrieval and RAG is the right technical bet while hallucination remains an unsolved problem at enterprise scale.",
  },
  {
    name: "Aleph Alpha",
    category: "Foundation Models",
    totalFundingM: 500,
    valuationM: 5800,
    employees: 120,
    founded: 2019,
    latestRound: "500M EUR Series B (2023)",
    insight: "11.6x multiple. European sovereign AI play with genuine government contract moat. GDPR and AI Act compliance baked in from day one. Overlooked by US-centric investors.",
  },
  {
    name: "AI21 Labs",
    category: "Foundation Models",
    totalFundingM: 336,
    valuationM: 1400,
    employees: 200,
    founded: 2017,
    latestRound: "$155M Series C (2023)",
    insight: "4.2x multiple. Jamba architecture shows real technical differentiation on long-context tasks. Pivoting toward enterprise API from consumer product is the right strategic call.",
  },

  // ── AI Applications ────────────────────────────────────────────────────────
  {
    name: "Cursor",
    category: "AI Applications",
    totalFundingM: 3400,
    valuationM: 55000,
    employees: 150,
    founded: 2022,
    latestRound: "$2.3B Series D (Nov 2025)",
    insight: "16.2x multiple with 150 people. Best capital efficiency in software at this valuation. Workflow lock-in is real. Developers do not switch IDEs easily. $500M ARR in 3 years is a product-market fit signal that is hard to argue with.",
  },
  {
    name: "Perplexity",
    category: "AI Applications",
    totalFundingM: 1720,
    valuationM: 22600,
    employees: 1500,
    founded: 2022,
    latestRound: "$200M (Sep 2025)",
    insight: "13.1x multiple. Answer engine UX is genuinely differentiated from Google. Distribution remains the five-year problem, but 15M+ daily active users suggests the product has found its audience.",
  },
  {
    name: "Harvey",
    category: "AI Applications",
    totalFundingM: 1220,
    valuationM: 11000,
    employees: 1150,
    founded: 2022,
    latestRound: "$200M (Mar 2026)",
    insight: "9x multiple. Best execution in vertical AI. Proprietary legal data compounds as a moat with every matter filed. The model improves while competitors start from scratch.",
  },
  {
    name: "Sierra",
    category: "AI Applications",
    totalFundingM: 635,
    valuationM: 10000,
    employees: 300,
    founded: 2023,
    latestRound: "$350M (Sep 2025)",
    insight: "15.7x multiple. One of the most capital-efficient companies at its stage. Bret Taylor plus a clear enterprise wedge in customer experience AI. The category is massively underpenetrated.",
  },
  {
    name: "Glean",
    category: "AI Applications",
    totalFundingM: 765,
    valuationM: 7200,
    employees: 700,
    founded: 2019,
    latestRound: "$150M Series F (Jun 2025)",
    insight: "9.4x multiple. Enterprise search has natural data gravity. Deep integration across hundreds of SaaS tools creates switching costs that compound over time. Quiet but strong execution.",
  },
  {
    name: "ElevenLabs",
    category: "AI Applications",
    totalFundingM: 180,
    valuationM: 3300,
    employees: 200,
    founded: 2022,
    latestRound: "$80M Series B (2024)",
    insight: "18.3x multiple. Voice AI with best-in-class quality and the largest proprietary voice dataset in the industry. International expansion is the growth story. Dubbing and localization open up a massive TAM.",
  },
  {
    name: "Writer",
    category: "AI Applications",
    totalFundingM: 200,
    valuationM: 1900,
    employees: 200,
    founded: 2020,
    latestRound: "$200M Series C (2024)",
    insight: "9.5x multiple. Enterprise content AI with a full platform approach. Style guides and brand voice training create genuine switching costs that generic LLM wrappers cannot replicate.",
  },
  {
    name: "Cognition",
    category: "AI Applications",
    totalFundingM: 175,
    valuationM: 2000,
    employees: 30,
    founded: 2023,
    latestRound: "$175M (2024)",
    insight: "11.4x multiple with only 30 employees. Devin sparked the software engineering agent category. Whether the product lives up to the demo is the open question, but the team density is remarkable.",
  },
  {
    name: "Runway ML",
    category: "AI Applications",
    totalFundingM: 237,
    valuationM: 1500,
    employees: 200,
    founded: 2018,
    latestRound: "$141M Series C (2023)",
    insight: "6.3x multiple. Video generation is technically hard and Runway is genuinely ahead. Hollywood partnerships are the distribution wedge. Gen-3 Alpha quality is a real differentiator from open-source alternatives.",
  },
  {
    name: "Synthesia",
    category: "AI Applications",
    totalFundingM: 156,
    valuationM: 1000,
    employees: 300,
    founded: 2017,
    latestRound: "$90M Series C (2023)",
    insight: "6.4x multiple. AI video avatars for enterprise training and communications. High-volume, low-touch production use cases create strong retention. Enterprise sales motion is the right go-to-market.",
  },
  {
    name: "Pika",
    category: "AI Applications",
    totalFundingM: 135,
    valuationM: 650,
    employees: 50,
    founded: 2023,
    latestRound: "$80M Series A (2024)",
    insight: "4.8x multiple. Consumer video generation with strong early traction. Competing against Runway and Sora from OpenAI is the long-term challenge. Speed of iteration is the current advantage.",
  },
  {
    name: "Ideogram",
    category: "AI Applications",
    totalFundingM: 80,
    valuationM: 450,
    employees: 50,
    founded: 2023,
    latestRound: "$80M Series A (2024)",
    insight: "5.6x multiple. Best-in-class text rendering in AI image generation. Typography has been a known weakness across the category. Solving it creates a specific defensible niche in design and marketing workflows.",
  },

  // ── Inference / Hardware ───────────────────────────────────────────────────
  {
    name: "Groq",
    category: "Inference / Hardware",
    totalFundingM: 1750,
    valuationM: 6900,
    employees: 400,
    founded: 2016,
    latestRound: "$750M (Sep 2025)",
    insight: "3.9x multiple reflects hardware capital intensity. The LPU is the only purpose-built inference chip story outside of Nvidia. Hyperscalers will attempt to copy, but the head start is real.",
  },
  {
    name: "CoreWeave",
    category: "Inference / Hardware",
    totalFundingM: 12000,
    valuationM: 23000,
    employees: 1500,
    founded: 2017,
    latestRound: "IPO at $23B (Mar 2025)",
    insight: "1.9x multiple reflects GPU infrastructure capital intensity. Nvidia backing plus hyperscale GPU capacity makes CoreWeave the default for companies that cannot get capacity from the big three cloud providers.",
  },
  {
    name: "Together AI",
    category: "Inference / Hardware",
    totalFundingM: 534,
    valuationM: 3300,
    employees: 290,
    founded: 2022,
    latestRound: "$305M Series B (Feb 2025)",
    insight: "6.2x multiple. Strong developer adoption of OSS model hosting. Needs to establish an enterprise wedge before hyperscalers commoditize the managed inference layer.",
  },
  {
    name: "Modal Labs",
    category: "Inference / Hardware",
    totalFundingM: 111,
    valuationM: 2500,
    employees: 100,
    founded: 2021,
    latestRound: "$87M Series B (Sep 2025)",
    insight: "22.5x multiple. Highest capital efficiency in this dataset. $111M to a $2.5B valuation is extraordinary. Developer experience is the wedge and workflows built on Modal are sticky.",
  },
  {
    name: "Cerebras",
    category: "Inference / Hardware",
    totalFundingM: 720,
    valuationM: 4000,
    employees: 300,
    founded: 2016,
    latestRound: "$250M (2024)",
    insight: "5.6x multiple. Wafer-scale chip architecture is genuinely novel. IPO attempt was withdrawn in 2024. The hardware moat is real but the go-to-market path for chip companies is long.",
  },
  {
    name: "Fireworks AI",
    category: "Inference / Hardware",
    totalFundingM: 76,
    valuationM: 550,
    employees: 60,
    founded: 2022,
    latestRound: "$52M Series B (2024)",
    insight: "7.2x multiple. Speed and cost focus with a developer-first approach. Niche but credible. The question is whether they can hold this position as hyperscaler free tiers improve.",
  },

  // ── Data Infrastructure ────────────────────────────────────────────────────
  {
    name: "Scale AI",
    category: "Data Infrastructure",
    totalFundingM: 15900,
    valuationM: 29000,
    employees: 1000,
    founded: 2016,
    latestRound: "$14.3B from Meta (Jun 2025)",
    insight: "1.8x multiple. Intentionally capital-intensive. Data labeling at frontier scale requires massive human and compute resources. Government and defense contracts are underappreciated in the public narrative.",
  },
  {
    name: "Databricks",
    category: "Data Infrastructure",
    totalFundingM: 15300,
    valuationM: 62000,
    employees: 7000,
    founded: 2013,
    latestRound: "$10B Series J (Dec 2024)",
    insight: "4x multiple on $15.3B. The lakehouse architecture won the data warehouse debate. MLflow plus DBRX creates a full data-to-model stack. Data gravity makes this infrastructure nearly impossible to rip out once adopted.",
  },
  {
    name: "Hugging Face",
    category: "Data Infrastructure",
    totalFundingM: 395,
    valuationM: 4500,
    employees: 350,
    founded: 2016,
    latestRound: "$235M Series D (2023)",
    insight: "11.4x multiple. The OSS model hub has become infrastructure. Once a team standardizes on it, switching costs are massive. Developer network effects compound with every new model upload.",
  },
  {
    name: "dbt Labs",
    category: "Data Infrastructure",
    totalFundingM: 414,
    valuationM: 4200,
    employees: 600,
    founded: 2016,
    latestRound: "$222M Series D (2022)",
    insight: "10.1x multiple. dbt became the standard for data transformation. Every data team uses it. That kind of workflow lock-in compounds over years and is very hard to displace.",
  },
  {
    name: "Fivetran",
    category: "Data Infrastructure",
    totalFundingM: 730,
    valuationM: 5600,
    employees: 1200,
    founded: 2012,
    latestRound: "$565M Series D (2021)",
    insight: "7.7x multiple. Data pipeline infrastructure with deep integration moat across hundreds of connectors. Not glamorous but mission-critical. The kind of software that renews automatically because ripping it out is painful.",
  },
  {
    name: "Tecton",
    category: "Data Infrastructure",
    totalFundingM: 160,
    valuationM: 1000,
    employees: 150,
    founded: 2019,
    latestRound: "$100M Series C (2022)",
    insight: "6.25x multiple. Feature store infrastructure for production ML. Every serious ML team eventually needs this. Early mover in a necessary category with strong enterprise traction.",
  },

  // ── Vector DBs ─────────────────────────────────────────────────────────────
  {
    name: "Pinecone",
    category: "Vector DBs",
    totalFundingM: 138,
    valuationM: 750,
    employees: 200,
    founded: 2021,
    latestRound: "$100M Series B (2023)",
    insight: "5.4x multiple. First-mover advantage is fading as pgvector and Postgres integrations eat the low end. Must move up-stack into a full RAG platform to defend its position.",
  },
  {
    name: "Weaviate",
    category: "Vector DBs",
    totalFundingM: 68,
    valuationM: 200,
    employees: 150,
    founded: 2019,
    latestRound: "$50M Series C (Oct 2025)",
    insight: "2.9x multiple. Open-source plus multimodal search is a better moat than pure-play managed. Hybrid vector and keyword search is the right call for enterprise retrieval workloads.",
  },
  {
    name: "Qdrant",
    category: "Vector DBs",
    totalFundingM: 88,
    valuationM: 200,
    employees: 100,
    founded: 2021,
    latestRound: "$50M Series B (Mar 2026)",
    insight: "2.3x multiple. Rust-based performance is genuine technical differentiation. Growing developer adoption organically. The enterprise GTM push will be the real test of defensibility.",
  },
  {
    name: "Zilliz",
    category: "Vector DBs",
    totalFundingM: 113,
    valuationM: 300,
    employees: 200,
    founded: 2017,
    latestRound: "$60M Series C (2023)",
    insight: "2.7x multiple. The company behind Milvus, the most widely deployed open-source vector database. OSS adoption creates a pipeline into the managed cloud product. Strong in Asia-Pacific markets.",
  },

  // ── Orchestration ──────────────────────────────────────────────────────────
  {
    name: "LangChain",
    category: "Orchestration",
    totalFundingM: 260,
    valuationM: 1250,
    employees: 80,
    founded: 2023,
    latestRound: "$125M Series B (Oct 2025)",
    insight: "4.8x multiple. Ubiquitous but commoditizing fast. Framework abstraction is inherently low-moat as every LLM provider builds native SDKs. LangSmith observability is the real durable business here.",
  },
  {
    name: "LlamaIndex",
    category: "Orchestration",
    totalFundingM: 18,
    valuationM: 100,
    employees: 30,
    founded: 2022,
    latestRound: "$8.5M Seed (2023)",
    insight: "5.6x multiple on a tiny base. Data connectors and retrieval abstractions are the real value, not the chain abstraction. Still early but the developer adoption is genuine.",
  },

  // ── MLOps / Observability ──────────────────────────────────────────────────
  {
    name: "Weights & Biases",
    category: "MLOps / Observability",
    totalFundingM: 250,
    valuationM: 1700,
    employees: 600,
    founded: 2017,
    latestRound: "Acquired by CoreWeave (May 2025)",
    insight: "6.8x multiple at acquisition. The MLOps standard with network effects from shared experiment runs. CoreWeave acquiring them validates that observability is now infrastructure, not tooling.",
  },
  {
    name: "Arize AI",
    category: "MLOps / Observability",
    totalFundingM: 62,
    valuationM: 280,
    employees: 150,
    founded: 2020,
    latestRound: "$38M Series B (2023)",
    insight: "4.5x multiple. LLM observability is genuinely underbuilt. Running production AI without evals is flying blind. Early mover in a category that every enterprise AI team will eventually need.",
  },
  {
    name: "Monte Carlo",
    category: "MLOps / Observability",
    totalFundingM: 236,
    valuationM: 1600,
    employees: 300,
    founded: 2019,
    latestRound: "$135M Series D (2022)",
    insight: "6.8x multiple. Data observability is mission-critical for production ML. Monitors data pipelines for quality issues before they corrupt model outputs. Strong enterprise traction with Fortune 500.",
  },
  {
    name: "Braintrust",
    category: "MLOps / Observability",
    totalFundingM: 36,
    valuationM: 150,
    employees: 25,
    founded: 2023,
    latestRound: "$36M (2024)",
    insight: "4.2x multiple. Evals as a product is the right bet. The industry has not figured out what good LLM evaluation looks like yet. Early mover in a necessary but underdeveloped category.",
  },
];

const processedData = rawData.map(d => ({
  ...d,
  x: parseFloat(Math.log10(d.totalFundingM).toFixed(3)),
  y: parseFloat((d.valuationM / d.totalFundingM).toFixed(2)),
  r: Math.min(42, Math.max(7, Math.sqrt(d.employees) * 1.55)),
}));

const X_TICKS = [
  { val: Math.log10(20),     label: "$20M"  },
  { val: Math.log10(100),    label: "$100M" },
  { val: Math.log10(500),    label: "$500M" },
  { val: Math.log10(1000),   label: "$1B"   },
  { val: Math.log10(5000),   label: "$5B"   },
  { val: Math.log10(15000),  label: "$15B"  },
  { val: Math.log10(50000),  label: "$50B"  },
  { val: Math.log10(250000), label: "$250B" },
];

const formatXTick = (logVal) => {
  const closest = X_TICKS.reduce((a, b) =>
    Math.abs(b.val - logVal) < Math.abs(a.val - logVal) ? b : a
  );
  if (Math.abs(closest.val - logVal) < 0.08) return closest.label;
  const v = Math.pow(10, logVal);
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}B`;
  return `$${Math.round(v)}M`;
};

const CustomDot = ({ cx, cy, payload, activeCategory }) => {
  if (!cx || !cy) return null;
  const isActive = !activeCategory || activeCategory === payload.category;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={payload.r}
      fill={COLORS[payload.category]}
      opacity={isActive ? 0.76 : 0.09}
      stroke={isActive ? COLORS[payload.category] : "transparent"}
      strokeWidth={1.5}
      strokeOpacity={0.35}
      style={{ cursor: "pointer", transition: "opacity 0.18s" }}
    />
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const multiple = (d.valuationM / d.totalFundingM).toFixed(1);
  const fmt = v => v >= 1000 ? `$${(v / 1000).toFixed(0)}B` : `$${v}M`;
  return (
    <div className="landscape-tooltip">
      <div className="landscape-tooltip-name">{d.name}</div>
      <div className="landscape-tooltip-category" style={{ color: COLORS[d.category] }}>
        {d.category}
      </div>
      <div className="landscape-tooltip-meta">
        <span>Raised {fmt(d.totalFundingM)}</span>
        <span>·</span>
        <span>Val {fmt(d.valuationM)}</span>
        <span>·</span>
        <span style={{ fontWeight: 600, color: "#111" }}>{multiple}x</span>
      </div>
      <div className="landscape-tooltip-meta" style={{ marginBottom: "10px" }}>
        <span>Founded {d.founded}</span>
        <span>·</span>
        <span>~{d.employees.toLocaleString()} employees</span>
      </div>
      <div className="landscape-tooltip-insight">{d.insight}</div>
      <div className="landscape-tooltip-round">{d.latestRound}</div>
    </div>
  );
};

function AILandscape() {
  const [activeCategory, setActiveCategory] = useState(null);
  useScrollAnimation();

  const toggle = useCallback(cat => {
    setActiveCategory(prev => prev === cat ? null : cat);
  }, []);

  return (
    <Container fluid className="landscape-section">
      <Container>
        <div className="animate-on-scroll landscape-header">
          <h1 className="page-title">AI Landscape</h1>
          <p className="landscape-subtitle">
            41 companies mapped by total capital raised vs. valuation/funding multiple.
            Higher on the y-axis means more value created per dollar raised. Bubble size reflects team size.
            Data from public disclosures and press releases.
          </p>
          <div className="landscape-updated">Last updated: May 2026</div>
        </div>

        <div className="animate-on-scroll delay-1 landscape-filters">
          <button
            className={`landscape-filter-btn${!activeCategory ? " active" : ""}`}
            onClick={() => setActiveCategory(null)}
          >
            All
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`landscape-filter-btn${activeCategory === cat ? " active" : ""}`}
              onClick={() => toggle(cat)}
            >
              <span className="filter-dot" style={{ background: COLORS[cat] }} />
              {cat}
            </button>
          ))}
        </div>

        <div className="animate-on-scroll delay-2 landscape-chart-wrap">
          <ResponsiveContainer width="100%" height={560}>
            <ScatterChart margin={{ top: 24, right: 32, bottom: 52, left: 32 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis
                type="number"
                dataKey="x"
                domain={[1.1, 5.6]}
                ticks={X_TICKS.map(t => t.val)}
                tickFormatter={formatXTick}
                label={{
                  value: "Total Funding Raised (log scale)",
                  position: "insideBottom",
                  offset: -32,
                  fill: "#aaa",
                  fontSize: 11,
                  fontFamily: "Inter, sans-serif",
                }}
                tick={{ fontSize: 10, fill: "#bbb", fontFamily: "Inter, sans-serif" }}
              />
              <YAxis
                type="number"
                dataKey="y"
                domain={[0, 26]}
                ticks={[0, 5, 10, 15, 20, 25]}
                tickFormatter={v => `${v}x`}
                label={{
                  value: "Valuation / Funding Multiple",
                  angle: -90,
                  position: "insideLeft",
                  offset: -18,
                  fill: "#aaa",
                  fontSize: 11,
                  fontFamily: "Inter, sans-serif",
                }}
                tick={{ fontSize: 10, fill: "#bbb", fontFamily: "Inter, sans-serif" }}
                width={44}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ strokeDasharray: "3 3", stroke: "rgba(0,0,0,0.1)" }}
              />
              <ReferenceLine y={5}  stroke="rgba(0,0,0,0.05)" strokeDasharray="6 3" />
              <ReferenceLine y={10} stroke="rgba(0,0,0,0.05)" strokeDasharray="6 3" />
              <ReferenceLine y={15} stroke="rgba(0,0,0,0.05)" strokeDasharray="6 3" />
              <Scatter
                data={processedData}
                shape={props => <CustomDot {...props} activeCategory={activeCategory} />}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="animate-on-scroll delay-3 landscape-footnote">
          Valuations reflect most recent known funding rounds. Scale AI reflects Meta $14.3B equity stake (Jun 2025).
          W&B reflects CoreWeave acquisition price. CoreWeave reflects IPO valuation. Hover any bubble for the full breakdown.
          <br /><br />
          Note: Google DeepMind (Gemini), Amazon Bedrock, and Microsoft Azure AI are not plotted. These are wholly-owned
          divisions of public companies with no independent funding rounds or auditable standalone valuations.
          Plotting them would require fabricating a number. Anthropic and Hugging Face, which operate as independent
          companies with disclosed venture rounds, are included.
        </div>

        <div className="animate-on-scroll landscape-legend">
          {CATEGORIES.map(cat => (
            <div key={cat} className="landscape-legend-item">
              <span className="landscape-legend-dot" style={{ background: COLORS[cat] }} />
              <span>{cat}</span>
            </div>
          ))}
        </div>

        <div className="animate-on-scroll landscape-thesis-cta">
          <p className="landscape-thesis-prompt">
            Want to understand what the data actually means? I wrote up my convictions on where each layer of this stack is heading.
          </p>
          <Button variant="primary" as={Link} to="/memo">
            read the memo
          </Button>
        </div>
      </Container>
    </Container>
  );
}

export default AILandscape;
